/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  patchCompositionsMoubusUnit,
  getCompositionsMoubusUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {format, pullDownMenu} from '../../define';
import {modbusInfoSort} from '../../functions/sort';

/**
 * 室外機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const OUEditDialog = (props: {
  /** 修正する室外機ID */
  ouID: string;
  /** 修正する室外機の情報 */
  displayOUInfo: apiIF.resParameters.modbusUnitInformations;
  /** 修正する空調機の情報（set関数） */
  setModbusInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsMoubusUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID} = useParams();

  //編集前の室外機情報を取得
  const tempOUName =
    props.displayOUInfo['outdoor-unit-name'] === undefined
      ? ''
      : props.displayOUInfo['outdoor-unit-name'];
  const tempIPAddress =
    props.displayOUInfo['ip-address'] === undefined
      ? ''
      : props.displayOUInfo['ip-address'];
  const tempOUModel =
    props.displayOUInfo['outdoor-unit-type'] === undefined
      ? 'HCCVQ'
      : props.displayOUInfo['outdoor-unit-type'];
  const tempOUPowerSupply =
    props.displayOUInfo['power-supply'] === undefined
      ? 'threePhase'
      : props.displayOUInfo['power-supply'];
  const tempOUVoltage =
    props.displayOUInfo['input-voltage'] === undefined
      ? '0'
      : props.displayOUInfo['input-voltage'];

  const [ouName, setOUName] = useState(tempOUName); //室外機の名前
  const [ipAddress, setIPAddress] = useState(tempIPAddress); //IPアドレス
  const [ouModel, setOUModel] = useState(tempOUModel); //室外機のタイプ
  const [ouPowerSupply, setOUPowerSupply] = useState(tempOUPowerSupply); //電源の仕様
  const [ouVoltage, setOUVoltage] = useState(tempOUVoltage); //入力電圧
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [ouNameError, setOUNameError] = useState(false); //室外機の名前
  const [ipAddressError, setIPAddressError] = useState(false); //IPアドレス
  const [ouVoltageError, setOUVoltageError] = useState(false); //入力電圧

  //入力項目のチェック
  useEffect(() => {
    if (
      ouName === '' ||
      ipAddress === '' ||
      ouNameError ||
      apiWait ||
      ouVoltageError ||
      ipAddressError
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [ouName, ipAddress, ouNameError, ouVoltageError, apiWait, ipAddressError]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editOU = () => {
    //パスパラメータを取得できない場合
    //室外機の情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      props.displayOUInfo === null
    ) {
      return;
    }

    setAPIWait(true);

    //情報を修正
    const editData: apiIF.reqParameters.patchCompositionsMoubusUnit = {
      'outdoor-unit-name': ouName,
      'ip-address': ipAddress,
      'outdoor-unit-type': ouModel,
      'power-supply': ouPowerSupply,
      'input-voltage': ouVoltage,
      'indoor-unit-informations':
        props.displayOUInfo['indoor-unit-informations'],
    };
    patchCompositionsMoubusUnit(siteID, gwID, props.ouID, editData)
      .then(() => {
        //修正した情報を取得
        return getCompositionsMoubusUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        response = modbusInfoSort(response);
        props.setModbusInfo(response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setOUName(tempOUName);
    setIPAddress(tempIPAddress);
    setOUPowerSupply(tempOUPowerSupply);
    setOUVoltage(tempOUVoltage);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('OUEditDialog.modbusUnit.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログ内に表示するメッセージ */}
          <DialogContentText>
            {trans('OUEditDialog.modbusUnit.msg')}
          </DialogContentText>
          <Stack spacing={1.5}>
            {/* 室外機名称 */}
            <ChkInputTextbox
              setValue={setOUName}
              setError={setOUNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('OUEditDialog.modbusUnit.txtField.label.unitName')}
              value={ouName}
              id={`${tempOUName}editOuNameTextForm`}
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans(
                'OUEditDialog.modbusUnit.txtField.errHelperTxt'
              )}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* IPアドレス */}
            <ChkInputTextbox
              setValue={setIPAddress}
              setError={setIPAddressError}
              chkInputTxtOK={format.IPADDRESS_PATTERN_OK}
              type="text"
              label={trans('OUEditDialog.modbusUnit.txtField.label.ipAddress')}
              value={ipAddress}
              id="addHccvqIPAddressTextForm"
              class="gwSetting__contents__textBox--ipAddress"
              helperText=" "
              errHelperText={trans(
                'OUEditDialog.modbusUnit.txtField.errHelperTxt'
              )}
              variant="standard"
              margin="dense"
            />
            {/* 室外機タイプ */}
            <TextFieldSelectBox
              label={trans('OUEditDialog.modbusUnit.txtField.label.unitType')}
              choices={pullDownMenu.MODBUS_OU_TYPE}
              setValue={setOUModel}
              id={`${tempOUName}EditOuTypeSelect`}
              class="gwSetting__contents__selectBox--modbusOUType"
            />
            {/* 電圧 */}
            <ChkInputTextbox
              setValue={setOUVoltage}
              setError={setOUVoltageError}
              chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
              type="text"
              label={trans('OUEditDialog.modbusUnit.txtField.label.voltage')}
              value={ouVoltage}
              id={`${tempOUName}EditVoltageInput`}
              class="gwSetting__contents__textBox--voltage"
              helperText=" "
              errHelperText={trans(
                'OUEditDialog.modbusUnit.txtField.errHelperTxt'
              )}
              variant="standard"
            />
            {/* 電源の種類 */}
            <TextFieldSelectBox
              label={trans(
                'OUEditDialog.modbusUnit.txtField.label.powerSupply'
              )}
              choices={pullDownMenu.POWER_SUPPLY_TYPE}
              setValue={setOUPowerSupply}
              default={ouPowerSupply}
              id="mv-ouAddPowSplySelect"
              class="gwSetting__contents__selectBox--powerSupply"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('OUEditDialog.buttonTxt.cancel')}
            id={`${tempOUName}EditCancelBtn`}
          />
          <PrimaryButton
            onClick={editOU}
            buttonText={trans('OUEditDialog.buttonTxt.ok')}
            id={`${tempOUName}EditOkBtn`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempOUName}EditErrDlgBtn`}
      />
    </>
  );
};
export default OUEditDialog;
